import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.js';
import {
  addressSchema,
  documentSchema,
  emailSchema,
  foreignTaxNumberSchema,
  licensesSchema,
  phoneSchema,
  RNOKPPIdentNumberSchema,
} from '../../validationSchemas';
import { regularExpressions } from '~/constants/regularExpressions';
import { latinAndUkrCharactersOnly } from '~/helpers/validation';
import { isBoolean } from 'lodash';

export const validationSchemaSoleTraderGeneralInfo = Yup.object().shape({
  fullName: Yup.string().required(errorMessages.FIELD_REQUIRED),
  address: Yup.object().shape({
    registrationAddress: addressSchema,
    permanentAddress: addressSchema,
    coincidences: Yup.boolean().nullable(),
    citizenshipCountry: Yup.object()
      .shape({
        value: Yup.string().nullable(),
        label: Yup.string().required(errorMessages.FIELD_REQUIRED),
      })
      .nullable()
      .required(errorMessages.FIELD_REQUIRED),
    residenceCountry: Yup.object()
      .shape({
        value: Yup.string().nullable(),
        label: Yup.string().required(errorMessages.FIELD_REQUIRED),
      })
      .nullable()
      .required(errorMessages.FIELD_REQUIRED),
    birthplaceCountry: Yup.object()
      .shape({
        value: Yup.string().nullable(),
        label: Yup.string().required(errorMessages.FIELD_REQUIRED),
      })
      .nullable()
      .required(errorMessages.FIELD_REQUIRED),
  }),
  birthDate: Yup.string().required(errorMessages.FIELD_REQUIRED),
  birthPlace: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .max(120, errorMessages.MAX_LENGTH(120)),
  uniqueDemographicRecordNumber: Yup.string().nullable(),
  documents: documentSchema,
  financialPhone: Yup.string().required(errorMessages.FIELD_REQUIRED).concat(phoneSchema),
  contactNumbers: Yup.array()
    .of(Yup.lazy((value) => (value && value.length > 4 ? phoneSchema : Yup.string().notRequired())))
    .notRequired(),
  emailAddress: Yup.array()
    .of(emailSchema)
    .min(1, errorMessages.EMAIL_ADDRESS_REQUIRED)
    .required(errorMessages.FIELD_REQUIRED),
  license: Yup.boolean().nullable().required(errorMessages.YES_NO_REQUIRED),
  licenses: licensesSchema('license'),
  questionnaire: Yup.object().test('all-questions-answered', function (value) {
    if (!value || typeof value !== 'object') return false;
    const isAllAnswered = Object.values(value).every((question) => isBoolean(question));
    if (!isAllAnswered) {
      return this.createError({
        path: this.path,
        message: errorMessages.FILL_OUT_THE_SURVEY_FORM,
      });
    }
    return true;
  }),
});

export const validationSchemaAssetManagerGeneralInfo = Yup.object().shape({
  lastName: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .concat(latinAndUkrCharactersOnly)
    .matches(regularExpressions.ONLY_TEXT, errorMessages.ONLY_TEXT),
  firstName: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .concat(latinAndUkrCharactersOnly)
    .matches(regularExpressions.ONLY_TEXT, errorMessages.ONLY_TEXT),
  middleName: Yup.string()
    .nullable()
    .concat(latinAndUkrCharactersOnly)
    .transform((_, orin) => (orin?.trim() === '' ? null : orin))
    .matches(regularExpressions.ONLY_TEXT, errorMessages.ONLY_TEXT),
  address: Yup.object().shape({
    registrationAddress: addressSchema,
    permanentAddress: addressSchema,
    coincidences: Yup.boolean().nullable(),
  }),
  identificationNumber: RNOKPPIdentNumberSchema('foreignTaxNumber'),
  foreignTaxNumber: foreignTaxNumberSchema('identificationNumber'),
  birthDate: Yup.string().required(errorMessages.FIELD_REQUIRED),
  birthPlace: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .max(120, errorMessages.MAX_LENGTH(120)),
  ident: Yup.string().nullable(),
  documents: documentSchema,
  contactNumbers: Yup.array()
    .of(Yup.lazy((value) => (value && value.length > 4 ? phoneSchema : Yup.string().notRequired())))
    .notRequired(),
  emailAddress: Yup.array()
    .of(emailSchema)
    .min(1, errorMessages.EMAIL_ADDRESS_REQUIRED)
    .required(errorMessages.FIELD_REQUIRED),
});

const fatcaConflictTest = (message: string) => ({
  name: 'fatca-only-one-yes',
  message,
  test(this: Yup.TestContext) {
    const { isUSTaxResident, isForeignFinancialUnderFATCA } = this.parent;
    return !(isUSTaxResident && isForeignFinancialUnderFATCA);
  },
});

export const validationSchemaLegalResidentGeneralInfo = (
  isAdditionalInformationAboutTaxResidencyEnabled: boolean,
  isFATCALegalResidentEnabled: boolean
) =>
  Yup.object().shape({
    fullNameUkr: Yup.string().required(errorMessages.FIELD_REQUIRED),
    fullNameEng: Yup.string()
      .nullable()
      .max(38, errorMessages.MAX_LENGTH(38))
      .matches(regularExpressions.LATIN_CHARACTERS_ONLY, errorMessages.LATIN_CHARACTERS_ONLY),
    shortNameUkr: Yup.string()
      .required(errorMessages.FIELD_REQUIRED)
      .max(38, errorMessages.MAX_LENGTH(38))
      .matches(regularExpressions.UKR_CHARACTERS_ONLY, errorMessages.UKR_CHARACTERS_ONLY),
    employeeCount: Yup.string()
      .required(errorMessages.FIELD_REQUIRED)
      .test('is-integer', function (value) {
        if (regularExpressions.INTEGER.test(value)) return true;
        else
          return this.createError({
            path: this.path,
            message: errorMessages.MUST_BE_INTEGER,
          });
      })
      .test('is-greater-than-zero', function (value) {
        if (+value > 0) return true;
        else
          return this.createError({
            path: this.path,
            message: errorMessages.MUST_BE_GREATER_THAN_ZERO,
          });
      }),
    address: Yup.object().shape({
      registrationAddress: addressSchema,
      permanentAddress: addressSchema,
      coincidences: Yup.boolean().nullable(),
    }),
    financialPhone: Yup.string().required(errorMessages.FIELD_REQUIRED).concat(phoneSchema),
    contactNumbers: Yup.array()
      .of(
        Yup.lazy((value) => (value && value.length > 4 ? phoneSchema : Yup.string().notRequired()))
      )
      .notRequired(),
    emailAddress: Yup.array()
      .of(emailSchema)
      .min(1, errorMessages.EMAIL_ADDRESS_REQUIRED)
      .required(errorMessages.FIELD_REQUIRED),
    questionnaire: Yup.object().test('all-questions-answered', function (value) {
      if (!value || typeof value !== 'object') return false;
      const isAllAnswered = Object.values(value).every(
        (question) => question || question === false
      );
      if (!isAllAnswered) {
        return this.createError({
          path: this.path,
          message: errorMessages.FILL_OUT_THE_SURVEY_FORM,
        });
      }
      return true;
    }),
    isOtherTaxResident: isAdditionalInformationAboutTaxResidencyEnabled
      ? Yup.boolean().required(errorMessages.YES_NO_REQUIRED)
      : Yup.boolean().notRequired(),
    isControllingPersonTaxResidentOtherJurisdiction: isAdditionalInformationAboutTaxResidencyEnabled
      ? Yup.boolean().required(errorMessages.YES_NO_REQUIRED)
      : Yup.boolean().notRequired(),
    isUSTaxResident: isFATCALegalResidentEnabled
      ? Yup.boolean()
          .required(errorMessages.YES_NO_REQUIRED)
          .test(fatcaConflictTest(errorMessages.FATCA_ONLY_ONE_YES))
      : Yup.boolean().notRequired(),
    isForeignFinancialUnderFATCA: isFATCALegalResidentEnabled
      ? Yup.boolean()
          .required(errorMessages.YES_NO_REQUIRED)
          .test(fatcaConflictTest(errorMessages.FATCA_ONLY_ONE_YES))
      : Yup.boolean().notRequired(),
    isControllingPersonUSTaxResident: isFATCALegalResidentEnabled
      ? Yup.boolean().required(errorMessages.YES_NO_REQUIRED)
      : Yup.boolean().notRequired(),
  });
